import type { ReactNode } from 'react'

import { EncuadreMotif } from '@/components/encuentro/page/encuadre-motif'
import { PageActionButton, type PageAction } from '@/components/encuentro/page/page-action'
import { cn } from '@/lib/utils'

type StateContent = {
  /** What is missing: "Todavía no hay eventos este mes". */
  title: string
  /** One true sentence. It never promises activity that does not exist. */
  body: ReactNode
  /** The one thing to do about it. Required: an empty state always offers a next step. */
  action: PageAction
}

export type EmptyStateProps = Omit<StateContent, 'action'> & {
  /**
   * The one thing to do about it. Left out only when nothing may be done: a
   * super admin viewing as a member (ALU-268), where every next step writes.
   */
  action?: PageAction
  /**
   * `h2` when the empty state stands in for a page's content under the
   * `PageHeader`, `h3` (default) inside a `Section`. Never `h1`: that is the
   * `PageHeader`'s, or `PageState`'s when the state is the whole page.
   */
  headingLevel?: 2 | 3
  /**
   * `primary` (default) when the action is the task of the page. `secondary`
   * when the page already shows its primary (one per screen).
   */
  actionVariant?: 'primary' | 'secondary'
  className?: string
}

/**
 * What is missing and what to do about it, with the Encuadre motif
 * (docs/redesign/sistema/EmptyState.md): title, one sentence and one action.
 * A card on the page surface. No mascots, no second button.
 */
export function EmptyState({
  title,
  body,
  action,
  headingLevel = 3,
  actionVariant = 'primary',
  className,
}: EmptyStateProps) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3'
  return (
    <div
      data-slot="empty-state"
      className={cn(
        'grid min-w-0 grid-cols-[minmax(0,1fr)] justify-items-center gap-3 rounded-en-lg bg-en-surface px-4 py-10 text-center md:px-6 md:py-12 font-en-sans text-en-ink shadow-en-card',
        className
      )}
    >
      <EncuadreMotif />
      <Heading className="text-en-subheading [overflow-wrap:anywhere]">{title}</Heading>
      <p className="max-w-[42ch] text-en-body text-en-ink-muted">{body}</p>
      {action && <PageActionButton action={action} variant={actionVariant} className="mt-2" />}
    </div>
  )
}

export type PageStateProps = StateContent & {
  /** A second, quieter way out ("Ir al inicio" under "Volver a intentar"). */
  secondaryAction?: PageAction
  /** A quiet line under the body (an error's reference). */
  note?: ReactNode
  className?: string
}

/**
 * A state that is the whole page: not found, a screen not rebuilt yet. Same
 * parts as `EmptyState`, but its title is the page's `h1`, so a page renders
 * either a `PageHeader` or a `PageState`, never both.
 */
export function PageState({
  title,
  body,
  action,
  secondaryAction,
  note,
  className,
}: PageStateProps) {
  return (
    <div
      data-slot="page-state"
      className={cn(
        'mx-auto grid w-full min-w-0 max-w-lg grid-cols-[minmax(0,1fr)] justify-items-center gap-4 px-4 py-16 text-center font-en-sans text-en-ink',
        className
      )}
    >
      <EncuadreMotif className="h-24 w-32" />
      {/* Hyphenated only with large text on a phone, as PageHeader's title. */}
      <h1 className="text-balance text-en-title [overflow-wrap:anywhere] en-large-text:hyphens-auto">
        {title}
      </h1>
      <p className="max-w-[42ch] text-en-body text-en-ink-muted">{body}</p>
      {note}
      {secondaryAction ? (
        <div className="mt-2 grid w-full grid-cols-[minmax(0,1fr)] gap-2 sm:flex sm:w-auto sm:flex-wrap sm:justify-center">
          <PageActionButton action={action} variant="primary" />
          <PageActionButton action={secondaryAction} variant="secondary" />
        </div>
      ) : (
        <PageActionButton action={action} variant="primary" className="mt-2" />
      )}
    </div>
  )
}
